import { BaseOrgLimitCard, type LimitField } from '../components'
import { useUpdateOrgLimit } from '@/services/settings'
import { useActiveOrgProfile } from '@/hooks/useActiveOrgProfile'
import { z } from 'zod'

type OrgServerLimitCardProps = {
  onLimitChange: (limit: number) => void
}

const serverLimitFormSchema = z.object({
  serverLimit: z.number().min(1, 'Server limit must be at least 1'),
})

export const OrgServerLimitCard = ({
  onLimitChange,
}: OrgServerLimitCardProps) => {
  const { data: activeOrgProfile, isLoading } = useActiveOrgProfile()

  const currentLimits = {
    serverLimit:
      activeOrgProfile?.org?.metadata?.limits?.org_per_user_server_count || 0,
  }

  const limitFields: LimitField[] = [
    {
      name: 'serverLimit',
      label: 'New Server Limit',
      unit: 'active servers per user',
      step: 1,
      minValue: 10,
      testId: 'server-limit-input',
    },
  ]

  const updateMutation = useUpdateOrgLimit()

  const confirmationConfig = {
    title: 'Confirm Organization-Wide Update',
    description: (
      watchedValues: { serverLimit: number },
      currentLimits: { serverLimit: number }
    ) =>
      `You're about to change the server limit from ${currentLimits.serverLimit} to ${watchedValues.serverLimit} active servers per user for all users.`,
    confirmText: (watchedValues: { serverLimit: number }) =>
      watchedValues.serverLimit.toString(),
    confirmPrompt: (watchedValues: { serverLimit: number }) =>
      `Please type "${watchedValues.serverLimit}" to confirm:`,
    warningPoints: [
      'All customized user limits will be overwritten',
      'This limit will apply to future resources. Existing resources will continue running based on the limits from when they were created.',
    ],
  }

  const testIds = {
    editButton: 'edit-org-server-limit-button',
    cancelButton: 'cancel-server-limit-button',
    applyButton: 'apply-server-limit-button',
  }

  const wrappedUpdateMutation = {
    mutate: (
      data: { serverLimit: number },
      options?: { onSuccess?: () => void; onError?: (error: any) => void }
    ) => {
      updateMutation.mutate(
        { org_per_user_server_count: data.serverLimit },
        options
      )
    },
    isPending: updateMutation.isPending,
  }

  return (
    <BaseOrgLimitCard
      isLoading={isLoading}
      currentLimits={currentLimits}
      limitFields={limitFields}
      formSchema={serverLimitFormSchema}
      onLimitChange={onLimitChange}
      updateMutation={wrappedUpdateMutation}
      confirmationConfig={confirmationConfig}
      editButtonLabel="Edit Organization Server Limit"
      testIds={testIds}
    />
  )
}
